import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { X, ExternalLink, Send, Loader2 } from 'lucide-react';
import { garageAPI } from '../../../services/garage/api';
import '../AIGarage.css';

const PHASE = {
  CONFIRM: 'confirm',
  PROCESSING: 'processing',
  REVIEW: 'review',
  SUCCESS: 'success',
};

const PROGRESS_STEPS = [
  'Gathering request context…',
  'Calling DOT AI Intake Assist…',
  'Formatting business justification & ROI…',
  'Preparing editable draft…',
];

const emptyDraft = (idea) => ({
  project_name: idea?.title || '',
  description: idea?.description || '',
  business_justification: idea?.description || '',
  solution_and_outcomes: '',
  priority: null,
  intake_extensions: {
    hard_roi_lines: [
      { category: 'new_revenue', annual_value_usd: 0 },
      { category: 'cost_savings', annual_value_usd: 0 },
      { category: 'non_compliance', annual_value_usd: 0 },
    ],
    soft_roi_types: [],
    soft_roi_estimated_annual_usd: 0,
    soft_roi_measurement_overview: '',
    hard_roi_calculation_details: '',
    requirement_details: [],
    requested_delivery_date: idea?.requestedDeliveryDate || '',
  },
});

/**
 * Module-level job store so "Run in background" survives modal dismiss
 * within the same SPA session.
 */
const escalateJobs = new Map();

export function getEscalateJob(ideaId) {
  return escalateJobs.get(ideaId) || null;
}

const EscalateToDotModal = ({
  isOpen,
  idea,
  adminEmail,
  adminName,
  onClose,
  onSuccess,
  onBackground,
}) => {
  const [phase, setPhase] = useState(PHASE.CONFIRM);
  const [deliveryDate, setDeliveryDate] = useState(idea?.requestedDeliveryDate || '');
  const [draft, setDraft] = useState(() => emptyDraft(idea));
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [progressIndex, setProgressIndex] = useState(0);
  const [result, setResult] = useState(null);
  const progressTimer = useRef(null);
  const assistAbort = useRef(false);

  const alreadyEscalated = Boolean(idea?.dotPublicUuid || idea?.dotPublicUrl);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  useEffect(() => {
    if (!isOpen || !idea?.id) return undefined;

    // Resume an in-flight / completed background job
    const job = escalateJobs.get(idea.id);
    if (job?.status === 'ready' && job.draft) {
      setDraft(job.draft);
      setNotice(job.notice || null);
      setDeliveryDate(job.requestedDeliveryDate || idea.requestedDeliveryDate || '');
      setPhase(PHASE.REVIEW);
    } else if (job?.status === 'processing') {
      setPhase(PHASE.PROCESSING);
      setProgressIndex(job.progressIndex || 0);
    } else if (job?.status === 'done' && job.result) {
      setResult(job.result);
      setPhase(PHASE.SUCCESS);
    } else if (idea.dotPublicUuid) {
      setResult({
        dotRefNumber: idea.dotRefNumber,
        dotPublicUrl: idea.dotPublicUrl,
      });
      setPhase(PHASE.SUCCESS);
    } else {
      setPhase(PHASE.CONFIRM);
      setDraft(emptyDraft(idea));
      setDeliveryDate(idea.requestedDeliveryDate || '');
      setNotice(null);
      setError(null);
      setResult(null);
    }

    return undefined;
  }, [isOpen, idea?.id]);

  useEffect(() => {
    if (phase !== PHASE.PROCESSING) {
      if (progressTimer.current) clearInterval(progressTimer.current);
      return undefined;
    }
    progressTimer.current = setInterval(() => {
      setProgressIndex((idx) => {
        const next = Math.min(idx + 1, PROGRESS_STEPS.length - 1);
        const job = escalateJobs.get(idea.id);
        if (job) job.progressIndex = next;
        return next;
      });
    }, 8000);
    return () => {
      if (progressTimer.current) clearInterval(progressTimer.current);
    };
  }, [phase, idea?.id]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && phase !== PHASE.PROCESSING && !submitting) onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose, phase, submitting]);

  const hardRoiLines = useMemo(
    () => draft?.intake_extensions?.hard_roi_lines || [],
    [draft]
  );

  const updateDraftField = (key, value) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const updateExtension = (key, value) => {
    setDraft((prev) => ({
      ...prev,
      intake_extensions: {
        ...(prev.intake_extensions || {}),
        [key]: value,
      },
    }));
  };

  const updateHardRoi = (category, annualValue) => {
    setDraft((prev) => {
      const lines = [...(prev.intake_extensions?.hard_roi_lines || [])];
      const idx = lines.findIndex((l) => l.category === category);
      const next = {
        category,
        annual_value_usd: Number.isFinite(Number(annualValue)) ? Number(annualValue) : 0,
      };
      if (idx >= 0) lines[idx] = next;
      else lines.push(next);
      return {
        ...prev,
        intake_extensions: {
          ...(prev.intake_extensions || {}),
          hard_roi_lines: lines,
        },
      };
    });
  };

  const startAssist = async () => {
    if (!idea?.id) return;
    setError(null);
    setNotice(null);
    setPhase(PHASE.PROCESSING);
    setProgressIndex(0);
    assistAbort.current = false;

    escalateJobs.set(idea.id, {
      status: 'processing',
      progressIndex: 0,
      startedAt: Date.now(),
    });

    try {
      const response = await garageAPI.escalateToDotAssist(idea.id, {
        requestedDeliveryDate: deliveryDate || undefined,
      });

      const nextDraft = response.draft || emptyDraft(idea);
      escalateJobs.set(idea.id, {
        status: 'ready',
        draft: nextDraft,
        notice: response.notice || null,
        requestedDeliveryDate: response.requestedDeliveryDate || deliveryDate || null,
      });

      if (assistAbort.current) {
        // User chose background — parent may reopen later
        onBackground?.(idea.id);
        return;
      }

      setDraft(nextDraft);
      setNotice(response.notice || null);
      if (response.requestedDeliveryDate) setDeliveryDate(response.requestedDeliveryDate);
      setPhase(PHASE.REVIEW);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.dotPublicUrl) {
        escalateJobs.set(idea.id, {
          status: 'done',
          result: {
            dotRefNumber: detail.dotRefNumber,
            dotPublicUrl: detail.dotPublicUrl,
          },
        });
        setResult({
          dotRefNumber: detail.dotRefNumber,
          dotPublicUrl: detail.dotPublicUrl,
        });
        setError(detail.message || 'Already escalated');
        setPhase(PHASE.SUCCESS);
        return;
      }
      escalateJobs.delete(idea.id);
      const message =
        (typeof detail === 'string' && detail) ||
        detail?.message ||
        err.message ||
        'Failed to generate draft';
      setError(message);
      setPhase(PHASE.CONFIRM);
    }
  };

  const handleRunInBackground = () => {
    assistAbort.current = true;
    onBackground?.(idea.id);
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const payloadDraft = {
        ...draft,
        intake_extensions: {
          ...(draft.intake_extensions || {}),
          requested_delivery_date:
            deliveryDate || draft.intake_extensions?.requested_delivery_date || undefined,
        },
      };
      const response = await garageAPI.escalateToDotSubmit(idea.id, {
        draft: payloadDraft,
        requestedDeliveryDate: deliveryDate || undefined,
      });
      escalateJobs.set(idea.id, { status: 'done', result: response });
      setResult(response);
      setPhase(PHASE.SUCCESS);
      onSuccess?.(response);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const message =
        (typeof detail === 'string' && detail) ||
        detail?.message ||
        err.message ||
        'Failed to submit to DOT';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div
      className="garage-modal-overlay"
      onClick={phase === PHASE.PROCESSING || submitting ? undefined : onClose}
    >
      <div
        className={`garage-modal garage-dot-escalate-modal ${phase === PHASE.REVIEW ? 'garage-dot-escalate-modal-wide' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="garage-modal-header">
          <h2>Escalate to IT BizOps</h2>
          <button
            className="garage-modal-close"
            onClick={phase === PHASE.PROCESSING ? handleRunInBackground : onClose}
            disabled={submitting}
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="garage-modal-body">
          {phase === PHASE.CONFIRM && (
            <>
              <div className="garage-request-info-context">
                <p><strong>Request:</strong> {idea.title}</p>
                <p><strong>From:</strong> {idea.requesterName || idea.requesterEmail}</p>
                <p><strong>Persona:</strong> {idea.primaryPersona || '—'}</p>
              </div>

              {alreadyEscalated ? (
                <div className="garage-route-info-box">
                  <p>
                    This request was already escalated to IT BizOps.
                    {idea.dotRefNumber ? ` Reference: ${idea.dotRefNumber}.` : ''}
                  </p>
                  {idea.dotPublicUrl && (
                    <a href={idea.dotPublicUrl} target="_blank" rel="noopener noreferrer">
                      View DOT request <ExternalLink size={14} />
                    </a>
                  )}
                </div>
              ) : (
                <>
                  <div className="garage-route-info-box">
                    <p>
                      This will draft a full IT BizOps intake (with ROI) via DOT AI Assist,
                      let you review/edit it, then create a DOT request. Status will become
                      <strong> Escalated to IT Business Operations</strong>.
                    </p>
                  </div>

                  <div className="garage-form-group">
                    <label htmlFor="dot-delivery-date">
                      Requested delivery date <span className="garage-optional">(Recommended)</span>
                    </label>
                    <input
                      id="dot-delivery-date"
                      type="date"
                      className="garage-input"
                      value={deliveryDate || ''}
                      onChange={(e) => setDeliveryDate(e.target.value)}
                    />
                  </div>

                  {error && <div className="garage-error-message">❌ {error}</div>}

                  <div className="garage-modal-actions">
                    <button type="button" className="garage-btn garage-btn-secondary" onClick={onClose}>
                      Cancel
                    </button>
                    <button type="button" className="garage-btn garage-btn-primary" onClick={startAssist}>
                      Escalate this request
                    </button>
                  </div>
                </>
              )}
            </>
          )}

          {phase === PHASE.PROCESSING && (
            <div className="garage-dot-progress">
              <Loader2 className="garage-dot-progress-spinner" size={28} />
              <h3>Generating IT BizOps draft</h3>
              <p className="garage-dot-progress-step">{PROGRESS_STEPS[progressIndex]}</p>
              <div className="garage-dot-progress-bar">
                <div
                  className="garage-dot-progress-bar-fill"
                  style={{ width: `${((progressIndex + 1) / PROGRESS_STEPS.length) * 100}%` }}
                />
              </div>
              <p className="garage-field-hint">
                Assist can take 30–60+ seconds. You can keep working — we&apos;ll reopen review when ready.
              </p>
              <button
                type="button"
                className="garage-btn garage-btn-secondary"
                onClick={handleRunInBackground}
              >
                Run in background
              </button>
            </div>
          )}

          {phase === PHASE.REVIEW && (
            <form onSubmit={handleSubmit} className="garage-dot-review-form">
              {notice && <div className="garage-route-info-box">{notice}</div>}

              <div className="garage-form-group">
                <label htmlFor="dot-project-name">Project name</label>
                <input
                  id="dot-project-name"
                  className="garage-input"
                  value={draft.project_name || ''}
                  onChange={(e) => updateDraftField('project_name', e.target.value)}
                  required
                  disabled={submitting}
                />
                <small className="garage-field-hint">
                  [AI Garage] prefix is applied automatically on submit.
                </small>
              </div>

              <div className="garage-form-group">
                <label htmlFor="dot-description">Business problem</label>
                <textarea
                  id="dot-description"
                  className="garage-textarea"
                  rows={4}
                  value={draft.description || ''}
                  onChange={(e) => updateDraftField('description', e.target.value)}
                  disabled={submitting}
                />
              </div>

              <div className="garage-form-group">
                <label htmlFor="dot-justification">Business justification</label>
                <textarea
                  id="dot-justification"
                  className="garage-textarea"
                  rows={4}
                  value={draft.business_justification || ''}
                  onChange={(e) => updateDraftField('business_justification', e.target.value)}
                  disabled={submitting}
                />
              </div>

              <div className="garage-form-group">
                <label htmlFor="dot-solution">Solution and outcomes</label>
                <textarea
                  id="dot-solution"
                  className="garage-textarea"
                  rows={4}
                  value={draft.solution_and_outcomes || ''}
                  onChange={(e) => updateDraftField('solution_and_outcomes', e.target.value)}
                  disabled={submitting}
                />
              </div>

              <div className="garage-form-group">
                <label htmlFor="dot-delivery-date-review">Requested delivery date</label>
                <input
                  id="dot-delivery-date-review"
                  type="date"
                  className="garage-input"
                  value={deliveryDate || ''}
                  onChange={(e) => setDeliveryDate(e.target.value)}
                  disabled={submitting}
                />
              </div>

              <div className="garage-dot-roi-grid">
                {['new_revenue', 'cost_savings', 'non_compliance'].map((category) => {
                  const line = hardRoiLines.find((l) => l.category === category) || {};
                  return (
                    <div className="garage-form-group" key={category}>
                      <label htmlFor={`dot-roi-${category}`}>
                        Hard ROI — {category.replace(/_/g, ' ')} (USD / yr)
                      </label>
                      <input
                        id={`dot-roi-${category}`}
                        type="number"
                        className="garage-input"
                        value={line.annual_value_usd ?? 0}
                        onChange={(e) => updateHardRoi(category, e.target.value)}
                        disabled={submitting}
                      />
                    </div>
                  );
                })}
              </div>

              <div className="garage-form-group">
                <label htmlFor="dot-soft-roi">Soft ROI types (comma-separated)</label>
                <input
                  id="dot-soft-roi"
                  className="garage-input"
                  value={(draft.intake_extensions?.soft_roi_types || []).join(', ')}
                  onChange={(e) =>
                    updateExtension(
                      'soft_roi_types',
                      e.target.value
                        .split(',')
                        .map((s) => s.trim())
                        .filter(Boolean)
                    )
                  }
                  disabled={submitting}
                  placeholder="Employee efficiency, Cost Reduction"
                />
              </div>

              {error && <div className="garage-error-message">❌ {error}</div>}

              <div className="garage-modal-actions">
                <button
                  type="button"
                  className="garage-btn garage-btn-secondary"
                  onClick={onClose}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button type="submit" className="garage-btn garage-btn-primary" disabled={submitting}>
                  {submitting ? (
                    <>Submitting to DOT…</>
                  ) : (
                    <>
                      <Send size={16} style={{ marginRight: 6 }} />
                      Submit to IT BizOps
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

          {phase === PHASE.SUCCESS && (
            <div className="garage-success-state">
              <div className="garage-success-icon">✅</div>
              <h3>Escalated to IT Business Operations</h3>
              {result?.dotRefNumber && (
                <p>IT BizOps request: <strong>{result.dotRefNumber}</strong></p>
              )}
              {result?.dotPublicUrl && (
                <a
                  href={result.dotPublicUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="garage-monday-link"
                >
                  View public status <ExternalLink size={14} />
                </a>
              )}
              <div className="garage-modal-actions" style={{ marginTop: 16 }}>
                <button type="button" className="garage-btn garage-btn-primary" onClick={onClose}>
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default EscalateToDotModal;
